import { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Form, Input, Select, Button, Space, Spin, Alert, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getCustomer,
  createCustomer,
  updateCustomer,
  type CreateCustomerRequest,
  type UpdateCustomerRequest,
} from '@/api/customerApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

const STATUS_OPTIONS = ['active', 'inactive', 'prospect', 'lead', 'churned'];

interface CustomerFormValues {
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  industry?: string;
  address?: string;
  website?: string;
  source?: string;
  status?: string;
  notes?: string;
  assigned_to?: string;
  tags?: string[];
}

export default function CustomerForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<CustomerFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing customer for edit mode
  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getCustomer(id)
      .then((customer) => {
        form.setFieldsValue({
          name: customer.name,
          email: customer.email || undefined,
          phone: customer.phone || undefined,
          company: customer.company || undefined,
          industry: customer.industry || undefined,
          address: customer.address || undefined,
          website: customer.website || undefined,
          source: customer.source || undefined,
          status: customer.status || 'active',
          notes: customer.notes || undefined,
          assigned_to: customer.assigned_to || undefined,
          tags: (customer.tags?.length ?? 0) > 0 ? customer.tags : undefined,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: CustomerFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const data: UpdateCustomerRequest = {
          name: values.name,
          email: values.email || undefined,
          phone: values.phone || undefined,
          company: values.company || undefined,
          industry: values.industry || undefined,
          address: values.address || undefined,
          website: values.website || undefined,
          source: values.source || undefined,
          status: values.status || undefined,
          notes: values.notes || undefined,
          assigned_to: values.assigned_to || undefined,
          tags: values.tags || undefined,
        };
        await updateCustomer(id, data);
        message.success(t('customers.update_success'));
        navigate(`/customers/${id}`);
      } else {
        const data: CreateCustomerRequest = {
          name: values.name,
          email: values.email || undefined,
          phone: values.phone || undefined,
          company: values.company || undefined,
          industry: values.industry || undefined,
          address: values.address || undefined,
          website: values.website || undefined,
          source: values.source || undefined,
          status: values.status || undefined,
          notes: values.notes || undefined,
          assigned_to: values.assigned_to || undefined,
          tags: values.tags || undefined,
        };
        const created = await createCustomer(data);
        message.success(t('customers.create_success'));
        navigate(`/customers/${created.id}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('app.error');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Permission gate: redirect if no write permission (after all hooks to comply with Rules of Hooks)
  if (!hasPermission('customers', 'write')) {
    return <Navigate to="/customers" replace />;
  }

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(isEdit ? `/customers/${id}` : '/customers')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('customers.edit_title') : t('customers.create_title')}</h2>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ status: 'active' }}
        style={{ maxWidth: 800 }}
      >
        <Form.Item
          name="name"
          label={t('customers.field.name')}
          rules={[
            { required: true, message: t('customers.validation.name_required') },
            { max: 200, message: t('customers.validation.name_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="email"
          label={t('customers.field.email')}
          rules={[
            { type: 'email', message: t('customers.validation.email_invalid') },
            { max: 200, message: t('customers.validation.email_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="phone"
          label={t('customers.field.phone')}
          rules={[{ max: 50, message: t('customers.validation.phone_max_length') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="company"
          label={t('customers.field.company')}
          rules={[{ max: 200, message: t('customers.validation.company_max_length') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="industry"
          label={t('customers.field.industry')}
          rules={[{ max: 100, message: t('customers.validation.industry_max_length') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="address"
          label={t('customers.field.address')}
          rules={[{ max: 500, message: t('customers.validation.address_max_length') }]}
        >
          <TextArea rows={2} />
        </Form.Item>

        <Form.Item
          name="website"
          label={t('customers.field.website')}
          rules={[
            { type: 'url', message: t('customers.validation.website_invalid') },
            { max: 500, message: t('customers.validation.website_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="source"
          label={t('customers.field.source')}
          rules={[{ max: 100, message: t('customers.validation.source_max_length') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="status" label={t('customers.field.status')}>
          <Select>
            {STATUS_OPTIONS.map((s) => (
              <Select.Option key={s} value={s}>
                {t(`customers.status_options.${s}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="notes"
          label={t('customers.field.notes')}
          rules={[{ max: 2000, message: t('customers.validation.notes_max_length') }]}
        >
          <TextArea rows={4} />
        </Form.Item>

        <Form.Item name="assigned_to" label={t('customers.field.assigned_to')}>
          <Input />
        </Form.Item>

        <Form.Item name="tags" label={t('customers.field.tags')}>
          <Select mode="tags" tokenSeparators={[',']} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate(isEdit ? `/customers/${id}` : '/customers')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
